/**
 * Translation helpers for dashboard field labels and status strings.
 *
 * These are thin wrappers that convert dot-notation translation keys to
 * human-readable labels. In a future internationalisation pass, these
 * functions would look up keys in a locale bundle. For now they extract
 * the last segment of the key and title-case it, with a manual override
 * map for keys whose auto-derived form is not idiomatic English.
 */

/** Manual overrides where the auto-derived title-case form is not ideal. */
const FIELD_OVERRIDES: Record<string, string> = {
  "humanTask.kind.approval": "Approval",
  "humanTask.kind.outbound_approval": "Outbound Approval",
  "humanTask.kind.browser_handoff": "Browser Handoff",
  "humanTask.kind.clarification": "Clarification",
  "humanTask.kind.destructive_confirm": "Destructive Confirm",
};

const STATUS_OVERRIDES: Record<string, string> = {
  "humanTask.status.pending": "Pending",
  "humanTask.status.resolved": "Resolved",
  "humanTask.status.expired": "Expired",
  "humanTask.status.cancelled": "Cancelled",
};

/**
 * Converts a dot-notation field key to a human-readable label.
 * Returns the override if one exists, otherwise title-cases the last
 * segment (replacing underscores with spaces).
 */
export function tField(key: string): string {
  if (FIELD_OVERRIDES[key]) return FIELD_OVERRIDES[key];
  const last = key.split(".").pop() ?? key;
  return last
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Converts a dot-notation status key to a human-readable label.
 * Returns the override if one exists, otherwise title-cases the last
 * segment (replacing underscores with spaces).
 */
export function tStatus(key: string): string {
  if (STATUS_OVERRIDES[key]) return STATUS_OVERRIDES[key];
  const last = key.split(".").pop() ?? key;
  return last
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
